import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Clock, TrendingUp, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface LeadAssignmentLog {
  _id: string;
  date: string;
  marketingUser: string;
  leadsAssigned: number;
  type: 'manual' | 'bulk';
}

interface AdminStats {
  totalFreeUsers: number;
  assignedLeads: number;
  availableLeads: number;
  totalMarketingUsers: number;
  expiringSoon: number;
}

export default function AdminLeadsSettings() {
  const [leadExpiryDays, setLeadExpiryDays] = useState('14');
  const [showMyLeadsTab, setShowMyLeadsTab] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LeadAssignmentLog[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalFreeUsers: 0,
    assignedLeads: 0,
    availableLeads: 0,
    totalMarketingUsers: 0,
    expiringSoon: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const [settingsRes, logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/leads/admin/settings`),
        axios.get(`${API_URL}/leads/admin/logs`),
        axios.get(`${API_URL}/leads/admin/stats`)
      ]);

      if (settingsRes.data.success) {
        setLeadExpiryDays(settingsRes.data.data.leadExpiryDays.toString());
        setShowMyLeadsTab(settingsRes.data.data.showMyLeadsTab !== false); // Default to true if not set
      }

      if (logsRes.data.success) {
        setLogs(logsRes.data.data);
      }

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const days = parseInt(leadExpiryDays);
    if (isNaN(days) || days < 1 || days > 90) {
      toast.error('Please enter a valid number between 1 and 90');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      console.log('💾 Saving lead settings:', {
        leadExpiryDays: days,
        showMyLeadsTab: showMyLeadsTab
      });

      const response = await axios.put(`${API_URL}/leads/admin/settings`, {
        leadExpiryDays: days,
        showMyLeadsTab: showMyLeadsTab
      });

      console.log('✅ Settings saved response:', response.data);

      if (response.data.success) {
        toast.success('Settings saved successfully');
        // Refresh data to ensure we have the latest
        await fetchData();
      }
    } catch (error: any) {
      console.error('❌ Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLeadExpiryDays('14');
    setShowMyLeadsTab(true);
    toast.success('Settings reset to default');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-pink-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-gray-900 mb-2">Leads Settings</h1>
        <p className="text-gray-600">Configure lead assignment and expiry settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2 text-gray-600" />
                <CardTitle>Lead Expiry Configuration</CardTitle>
              </div>
              <CardDescription>
                Set how long leads remain assigned to marketing users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="expiry-days">Lead Assignment Validity (Days)</Label>
                <div className="flex gap-4 mt-2">
                  <Input
                    id="expiry-days"
                    type="number"
                    min="1"
                    max="90"
                    value={leadExpiryDays}
                    onChange={(e) => setLeadExpiryDays(e.target.value)}
                    className="max-w-32"
                  />
                  <span className="text-gray-600 flex items-center">days</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Leads will automatically be removed from marketing users after this period
                </p>
              </div>

              <Separator />

              <div className="flex items-center justify-between py-4">
                <div className="space-y-0.5">
                  <Label htmlFor="show-my-leads">Show "My Leads" Tab for Marketing Users</Label>
                  <p className="text-sm text-gray-500">
                    Enable or disable the My Leads tab visibility in marketing panel
                  </p>
                </div>
                <Switch
                  id="show-my-leads"
                  checked={showMyLeadsTab}
                  onCheckedChange={setShowMyLeadsTab}
                />
              </div>

              <Separator />

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-sm text-blue-900 mb-2">How it works:</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>When a lead is assigned to a marketing user, it has {leadExpiryDays} days validity</li>
                  <li>Marketing users can contact and follow up with leads during this period</li>
                  <li>After expiry, leads are automatically removed from marketing panel</li>
                  <li>Expired leads become available for reassignment</li>
                  <li>One lead can only be assigned to one marketing user at a time</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleReset}>
                  Reset to Default
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Assignment Logs */}
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-gray-600" />
                <CardTitle>Recent Assignment Activity</CardTitle>
              </div>
              <CardDescription>
                Track recent lead assignments to marketing users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Marketing User</TableHead>
                      <TableHead>Leads Assigned</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                          No assignment activity yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log._id}>
                          <TableCell className="text-sm">{log.date}</TableCell>
                          <TableCell>{log.marketingUser}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {log.leadsAssigned} leads
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.type === 'bulk' ? (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                Bulk
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Manual
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-gray-600" />
                <CardTitle>System Overview</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Free Users</span>
                  <span className="text-2xl">{stats.totalFreeUsers}</span>
                </div>
                <Separator />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Assigned Leads</span>
                  <span className="text-2xl text-green-600">{stats.assignedLeads}</span>
                </div>
                <Separator />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available Leads</span>
                  <span className="text-2xl text-orange-600">{stats.availableLeads}</span>
                </div>
                <Separator />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Marketing Users</span>
                  <span className="text-2xl">{stats.totalMarketingUsers}</span>
                </div>
                <Separator />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Expiring Soon</span>
                  <span className="text-2xl text-red-600">{stats.expiringSoon}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2">•</span>
                  <span>Regularly monitor expiring leads to maximize conversions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2">•</span>
                  <span>Use bulk assignment to distribute leads evenly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2">•</span>
                  <span>Adjust validity period based on team capacity</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-2">•</span>
                  <span>Track conversion rates in marketing panel</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}